import { useEffect, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Logo } from '@/shared/components/Logo'
import { useVerifyOtp } from '../hooks/useVerifyOtp'
import { useLogin } from '../hooks/useLogin'
import { useAuth } from '../context/AuthProvider'
import { tokenStorage } from '@/lib/auth/tokenStorage'
import type { AccountOTPVerifyInput } from '@/shared/types'

const RESEND_TIMER = 50

export function VerifyOTPPage() {
  const { phone } = useParams<{ phone: string }>()
  const navigate = useNavigate()
  const { login: authLogin } = useAuth()
  const [logoHref, setLogoHref] = useState('/')

  const [formData, setFormData] = useState<AccountOTPVerifyInput>({
    phone: phone || '',
    otp: '',
    role: 'VENDOR_OWNER',
  })

  const [timer, setTimer] = useState(RESEND_TIMER)

  const handleVerifySuccess = (token: string, redirectUrl: string) => {
    authLogin(token)
    const vendorMatch = redirectUrl.match(/\/dashboard\/([^/]+)/)
    if (vendorMatch?.[1]) {
      tokenStorage.setVendorId(vendorMatch[1])
    }
    navigate(redirectUrl)
  }

  const { verifyOtp, loading: loadingVerify } = useVerifyOtp({ onSuccess: handleVerifySuccess })

  const handleResendSuccess = () => {
    setTimer(RESEND_TIMER)
  }

  const { login: resendOtp, loading: loadingResend } = useLogin({ onSuccess: handleResendSuccess })

  const isLoading = loadingVerify || loadingResend

  useEffect(() => {
    if (timer <= 0) return

    const interval = setInterval(() => {
      setTimer((t) => t - 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [timer])

  useEffect(() => {
    const token = tokenStorage.getToken()
    const vendorId = tokenStorage.getVendorId()
    if (token && vendorId) {
      setLogoHref(`/dashboard/${vendorId}`)
    }
  }, [])

  const handleChange = (key: keyof AccountOTPVerifyInput, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg p-8 shadow-sm">
          <div className="text-center mb-8">
            <div className="w-full flex justify-center items-center mb-4">
              <Logo href={logoHref} />
            </div>
            <h1 className="text-2xl font-bold text-black">Verify OTP</h1>
            <p className="text-gray-600 mt-2">
              Enter the 6-digit OTP sent to <br /> your registered phone number{' '}
              <span className="font-semibold">{phone}</span>
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              verifyOtp(formData)
            }}
            className="space-y-6"
          >
            <div className="flex flex-col gap-2">
              <Label>One Time Password (OTP)</Label>
              <Input
                value={formData.otp}
                onChange={(e) => handleChange('otp', e.target.value)}
                maxLength={6}
                placeholder="Enter OTP"
                required
              />
            </div>

            <Button disabled={isLoading} type="submit" className="w-full bg-green-600 text-white cursor-pointer">
              {loadingVerify ? 'Verifying...' : 'Verify & Continue'}
            </Button>

            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Didn't receive the OTP?</span>

              {timer > 0 ? (
                <span className="text-gray-400">Resend in {timer}s</span>
              ) : (
                <button
                  type="button"
                  disabled={loadingResend}
                  onClick={() => resendOtp({ phone: phone || '' })}
                  className="text-[#2C9C46] hover:underline"
                >
                  {loadingResend ? 'Sending...' : 'Resend OTP'}
                </button>
              )}
            </div>

            <div className="text-center">
              <Link to="/login" className="text-gray-500 hover:underline">
                Change phone number
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
